import {useGrpcRequest} from '@aboutyou/data-fetcher';
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import {NavigationService_GetHeaderBulk} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.grpc';
import {GetHeaderBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {useOptionalSession} from '@aboutyou/user';
import {useSelectedTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';
import {useEntityIdentifier} from './useEntityIdentifier';
import {useSelectedDiscountRequestParameter} from '../../Campaigns/SelectedDiscounts/useSelectedDiscount';
import {useHeaderInMemoryCache} from './useHeaderInMemoryCache/useHeaderInMemoryCache';
import {startTransition, useEffect, useRef, useState} from 'react';
import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/target_group.types';

const useGetHeaderBulkSection = <Section extends keyof GetHeaderBulkResponse>(
  section: Section,
) => {
  const session = useOptionalSession();
  const {initialTargetGroup, switchTargetGroup} = useSelectedTargetGroup();
  const identifier = useEntityIdentifier();
  const includeFlyoutItems = !useIsSoftFeatureToggleActive(
    'disableDesktopHeaderFlyouts',
  );
  const selectedDiscount = useSelectedDiscountRequestParameter();

  const requestParams = {
    session,
    sessionTargetGroup: initialTargetGroup ?? null,
    includeFlyoutItems,
    identifier: identifier ?? null,
    selectedDiscount,
  };

  /**
   * This recommendation from the server render should only be set without breaking the hydration
   */
  const [
    recommendedTargetGroupCallback,
    setRecommendedTargetGroupAfterHydration,
  ] = useState<{
    recommendedTargetGroup: TargetGroup;
    invalidateCache: () => void;
  } | null>(null);
  const initialRecommendedTargetGroupSet = useRef(false);

  useEffect(() => {
    if (
      recommendedTargetGroupCallback &&
      !initialRecommendedTargetGroupSet.current
    ) {
      initialRecommendedTargetGroupSet.current = true;

      switchTargetGroup(recommendedTargetGroupCallback.recommendedTargetGroup);
      recommendedTargetGroupCallback.invalidateCache();
    }
  }, [recommendedTargetGroupCallback, switchTargetGroup]);

  const {setHeaderCache, validateInMemoryCacheRequest} =
    useHeaderInMemoryCache();

  const request = useGrpcRequest(
    NavigationService_GetHeaderBulk,
    requestParams,
    {
      section,
      validateInMemoryCacheRequest,
      onServerSuccess: async bulkData => {
        setHeaderCache(bulkData);
      },
      onRequestInitialDataSuccess: (
        initialData: GetHeaderBulkResponse,
        {invalidateCache, duplicateInitialDataByParams},
      ) => {
        startTransition(() => {
          const recommendedTargetGroup =
            initialData.genderSwitch.recommendedTargetGroup;

          if (section === 'genderSwitch') {
            // If there in no BE recommendedTargetGroup and we dont have a session targetGroup we assume that BE rendered the FEMALE targetGroup to render the header
            // and we use that data create a cache entry for the FEMALE targetGroup
            if (!recommendedTargetGroup && !initialTargetGroup) {
              duplicateInitialDataByParams({
                ...requestParams,
                sessionTargetGroup: initialData.genderSwitch.appliedTargetGroup,
              });
            }

            if (
              recommendedTargetGroup &&
              recommendedTargetGroup !== initialTargetGroup
            ) {
              // if we made the request with a different sessionTargetGroup (based on the cookie saved on the client) but the BE returned data based on the `identifier` for a different target group,
              // need to update the cache with the same data, but with the new sessionTargetGroup.
              // duplicateInitialDataByParams allows to duplicate the initial data with the new params.
              duplicateInitialDataByParams({
                ...requestParams,
                sessionTargetGroup: recommendedTargetGroup,
              });

              setRecommendedTargetGroupAfterHydration({
                // then switch the targetGroup recommended by the BE based on the `identifier`
                recommendedTargetGroup,
                // we should invalidate the cache for the initial request only after switching the gender(targetGroup), otherwise it will trigger another request with the old targetGroup
                invalidateCache,
              });
            }
          }
        });
      },
      // The `identifier` is only used for SEO and does not need to ever update on the client. This change makes sure we do not make a request when the identifier changes.
      // The request would still fire when other params like sessionTargetGroup, session changes.
      transformCacheKey(config) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {identifier, ...params} = config.params;
        return {
          ...config,
          params,
        };
      },
    },
  );

  return request;
};

export const useMetaBarSection = () =>
  useGetHeaderBulkSection('metaBarSection');

export const useGenderSwitchSection = () =>
  useGetHeaderBulkSection('genderSwitch');

export const useNavigationBarSection = () => useGetHeaderBulkSection('navBar');

export const useUSPSection = () => useGetHeaderBulkSection('uspsSection');
